import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { mainSiteUrl } from "@/mocks/categories";
import { blogArticleContent, blogArticleFaqs, allBlogArticles } from "@/mocks/blogContent";
import siteUrl from "@/config/site";

function Breadcrumb({ title }: { title: string }) {
  return (
    <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
      <Link to="/" className="hover:text-foreground-800 transition-colors whitespace-nowrap">
        Home
      </Link>
      <i className="ri-arrow-right-s-line text-xs"></i>
      <span className="text-foreground-900 font-medium whitespace-nowrap">Blog</span>
      <i className="ri-arrow-right-s-line text-xs"></i>
      <span className="text-foreground-700 truncate max-w-[280px]">{title}</span>
    </nav>
  );
}

export default function BlogArticle() {
  const { slug } = useParams<{ slug: string }>();

  const article = allBlogArticles.find((a) => a.slug === slug) || allBlogArticles[0];
  const isMainArticle = article.slug === "what-is-mulebuy-spreadsheet";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  return (
    <main>
      {/* React 19 native head tags */}
      <title>{article.title} | Mulebuy Spreadsheet</title>
      <meta name="description" content={article.excerpt} />
      <link rel="canonical" href={siteUrl + "/blog/" + article.slug} />

      {/* JSON-LD Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            author: { "@type": "Person", name: article.author },
            datePublished: "2026-06-22",
            dateModified: "2026-06-22",
            publisher: { "@type": "Organization", name: "Mulebuy Spreadsheet", url: siteUrl },
            mainEntityOfPage: { "@type": "WebPage", "@id": siteUrl + "/blog/" + article.slug }
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
              { "@type": "ListItem", position: 2, name: "Blog", item: siteUrl + "/blog" },
              { "@type": "ListItem", position: 3, name: article.title }
            ]
          })
        }}
      />
      {isMainArticle && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: blogArticleFaqs.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: { "@type": "Answer", text: faq.answer }
              }))
            })
          }}
        />
      )}

      <Navbar />

      {/* Article Header */}
      <section className="w-full px-6 md:px-10 pt-24 pb-10 md:pt-28 md:pb-14 bg-background-50">
        <Breadcrumb title={article.title} />
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
          {/* Left: Image */}
          <div className="lg:w-2/5">
            <span className="inline-block px-4 py-1.5 rounded-full border border-foreground-300/60 text-xs font-medium text-foreground-700 bg-white mb-6">
              Mulebuy Spreadsheet Guide
            </span>
            <div className="w-full h-64 md:h-96 rounded-[40px] overflow-hidden">
              <img
                src={article.cardImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Right: Title & Meta */}
          <div className="lg:w-3/5 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="text-xs font-medium text-primary-700 bg-primary-100/70 px-3 py-1 rounded-full whitespace-nowrap">
                SEO Guide
              </span>
              <span className="text-xs text-foreground-500 whitespace-nowrap">Updated June 2026</span>
              <span className="text-xs text-foreground-500 whitespace-nowrap">12 min read</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-xl">
              {article.excerpt}
            </p>

            {/* Quick nav */}
            <div className="flex flex-wrap gap-2 mt-6">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 border border-background-200/70 text-sm text-foreground-700 hover:bg-background-200/70 transition-all whitespace-nowrap"
              >
                <i className="ri-home-4-line text-sm"></i>
                Back to Mulebuy Spreadsheet Home
              </Link>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-white text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Visit Main Site
                <i className="ri-external-link-line text-sm"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Article Body */}
      <article className="w-full px-6 md:px-10 py-10 md:py-16 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-8 pb-6 border-b border-background-200/70">
            <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center overflow-hidden flex-shrink-0">
              <span className="text-accent-700 font-semibold">
                {article.author.split(" ").map((n) => n[0]).join("")}
              </span>
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-900">{article.author}</p>
              <p className="text-xs text-foreground-500">{article.role}</p>
            </div>
          </div>

          <div
            className="prose prose-lg max-w-none
              prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-bold
              prose-h2:text-2xl md:prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4
              prose-h3:text-xl md:prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5 prose-p:text-base md:prose-p:text-lg
              prose-a:text-primary-600 prose-a:no-underline hover:prose-a:text-primary-700
              prose-strong:text-foreground-950 prose-strong:font-semibold
              prose-table:border prose-table:border-background-200/70 prose-table:rounded-xl prose-table:overflow-hidden
              prose-th:bg-background-100 prose-th:text-foreground-800 prose-th:text-sm prose-th:font-medium prose-th:px-4 prose-th:py-3
              prose-td:text-foreground-700 prose-td:text-sm prose-td:px-4 prose-td:py-3 prose-td:border-t prose-td:border-background-200/70
              prose-ol:list-decimal prose-ol:pl-6 prose-ol:text-foreground-700
              prose-li:mb-2 prose-li:text-base"
            dangerouslySetInnerHTML={{ __html: blogArticleContent }}
          />

          {/* Inline internal links */}
          <div className="mt-12 p-6 bg-background-100 rounded-2xl border border-background-200/70">
            <p className="text-foreground-700 text-sm leading-relaxed mb-3">
              <strong className="text-foreground-950">Ready to explore more?</strong> Visit the{" "}
              <Link to="/" className="text-primary-600 font-medium hover:text-primary-700">
                mulebuy spreadsheet database
              </Link>{" "}
              homepage to browse all 14 product categories, or check out the{" "}
              <Link to="/" className="text-primary-600 font-medium hover:text-primary-700">
                updated product spreadsheet
              </Link>{" "}
              for the latest trending items curated weekly.
            </p>
          </div>

          {/* Related Articles */}
          <div className="mt-14">
            <h3 className="text-xl font-heading font-bold text-foreground-950 mb-6">
              More Mulebuy Spreadsheet Articles
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {allBlogArticles
                .filter((a) => a.slug !== article.slug)
                .slice(0, 4)
                .map((related) => (
                  <Link
                    key={related.slug}
                    to={`/blog/${related.slug}`}
                    className="bg-white rounded-xl p-4 border border-background-200/70 hover:border-primary-200 transition-all group cursor-pointer flex gap-4"
                  >
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={related.cardImage}
                        alt={related.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-foreground-500 mb-1">{related.author}</p>
                      <h4 className="text-sm font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors leading-snug line-clamp-2">
                        {related.title}
                      </h4>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </article>

      {/* FAQ Section - only for main article */}
      {isMainArticle && (
        <section className="w-full px-6 md:px-10 py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-10 text-center">
              Frequently Asked Questions About Mulebuy Spreadsheet
            </h2>
            <div className="space-y-3">
              {blogArticleFaqs.map((faq, idx) => (
                <details key={idx} className="group bg-white rounded-xl border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-50 transition-colors list-none">
                    <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-900 pr-4">
                      {faq.question}
                    </h3>
                    <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-full bg-background-100 group-open:bg-primary-100 transition-colors">
                      <i className="ri-add-line text-sm text-foreground-600 group-open:hidden"></i>
                      <i className="ri-subtract-line text-sm text-primary-600 hidden group-open:block"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Wide%20panoramic%20minimalist%20editorial%20photography%20of%20a%20stylish%20desk%20arrangement%20with%20fashion%20magazines%20laptop%20displaying%20product%20catalog%20and%20shopping%20bags%20on%20warm%20cream%20surface%20bright%20soft%20natural%20daylight%20airy%20atmosphere%20modern%20lifestyle%20aesthetic%20neutral%20earth%20tones&width=1600&height=600&seq=mulebuy-blog-cta-x2&orientation=landscape"
            alt="Mulebuy spreadsheet CTA"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        <div className="relative z-10 w-full px-6 md:px-16 py-16 md:py-24">
          <div className="text-center">
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-white mb-4">
              Explore More Articles
            </h2>
            <p className="text-white/80 text-sm md:text-base max-w-md mx-auto mb-8">
              Discover more insights about the mulebuy spreadsheet database and how it
              helps shoppers find trending products faster.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white text-foreground-950 font-medium text-sm hover:bg-white/90 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-4-line"></i>
                Back to Homepage
              </Link>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-accent-500 text-white font-medium text-sm hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Explore Mulebuy Spreadsheet
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-black/20">
                  <i className="ri-arrow-right-line text-xs"></i>
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}